import React, { useState } from 'react';
import { 
  Box, Button, TextField, Typography, Paper, 
  CircularProgress, Alert, IconButton, Card, CardContent,
  Divider, LinearProgress, Chip, Stack
} from '@mui/material';
import { CloudUpload, CheckCircle, Error, Download, Clear, Group } from '@mui/icons-material';

const BatchEnrollment = () => {
  const [batchId, setBatchId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please upload a valid CSV file');
      }
    }
  };

  const validateInputs = () => {
    if (!batchId.trim()) {
      setError('Please enter a valid batch ID');
      return false;
    }
    if (isNaN(parseInt(batchId))) {
      setError('Batch ID must be a valid number');
      return false;
    }
    if (!selectedFile) {
      setError('Please select a CSV file containing candidate details');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;

    try {
      setIsSubmitting(true);
      setError('');
      const token = localStorage.getItem('authToken');
      
      // Read and parse the CSV file to extract candidate IDs
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(selectedFile);
      });

      // Parse CSV content
      const lines = fileContent.split('\n').filter(line => line.trim());
      const candidateIds = [];
      
      // Skip header row (first line) and extract candidate IDs from second column
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line) {
          // Parse CSV line (handle quoted values)
          const columns = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
          if (columns.length >= 2) {
            // Extract candidate ID from second column, remove quotes if present
            let candidateId = columns[1].replace(/^"(.*)"$/, '$1').trim();
            if (candidateId) {
              candidateIds.push(candidateId);
            }
          }
        }
      }

      if (candidateIds.length === 0) {
        throw new Error('No valid candidate IDs found in the CSV file');
      }

      // Prepare the request payload
      const payload = {
        batchId: parseInt(batchId),
        candidateIds: candidateIds
      };

      console.log('Sending enrollment request:', payload);

      const response = await fetch('https://sims.ethnotech.in/api/enroll', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to process batch enrollment');
      }
      
      setResults({
        ...data,
        results: data.results || [],
        candidateIds: candidateIds // Store the extracted IDs for reference
      });
      
    } catch (err) {
      console.error('Enrollment error:', err);
      setError(err.message || 'An error occurred during batch enrollment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setBatchId('');
    setSelectedFile(null);
    setResults(null);
    setError('');
  };

  const handleDownload = () => {
    if (!results || !results.results) return;

    try {
      const csvHeaders = ['Candidate ID', 'Status', 'Message', 'Batch ID'];
      
      const csvRows = results.results.map(result => [
        result.candidateId || result.candidateID || 'N/A',
        result.success ? 'Success' : 'Failed',
        result.success ? 'Enrolled successfully' : (result.error || result.message || 'Unknown error'),
        batchId
      ].map(field => `"${String(field || '').replace(/"/g, '""')}"`));

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\r\n');

      // Create and trigger download
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch_enrollment_results_${batchId}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to generate download. Please try again.');
    }
  };

  return (
    <Box sx={{ 
      p: 4, 
      maxWidth: 1000, 
      mx: 'auto',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh',
      paddingTop: 2
    }}>
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Group sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Bulk Enrollment
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Enroll multiple candidates into training batches efficiently
          </Typography>
        </CardContent>
      </Card>
      
      <Card elevation={8} sx={{ 
        mb: 4, 
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            color: '#667eea'
          }}>
            📋 Bulk Enrollment Setup
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Enter batch ID and upload CSV file to enroll multiple candidates
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Batch ID"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              placeholder="Enter the Batch ID for enrollment"
              variant="outlined"
              disabled={isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)'
                }
              }}
            />
            
            <Box>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="csv-upload"
                type="file"
                onChange={handleFileChange}
                disabled={isSubmitting}
              />
              <label htmlFor="csv-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{ 
                    textTransform: 'none', 
                    py: 2,
                    px: 4,
                    borderRadius: 2,
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2 }
                  }}
                  disabled={isSubmitting}
                  fullWidth
                >
                  {selectedFile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle color="success" />
                      {selectedFile.name}
                    </Box>
                  ) : (
                    'Upload Candidate IDs (CSV)'
                  )}
                </Button>
              </label>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                📄 Upload a CSV file with format: User name, <strong>Candidate ID</strong>, Name prefix, First name<br/>
                <em>The system will extract Candidate IDs from the second column</em>
              </Typography>
            </Box>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || !batchId || !selectedFile}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <Group />}
              sx={{ 
                px: 4, 
                py: 1.5, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              {isSubmitting ? 'Processing Enrollment...' : 'Enroll Candidates'}
            </Button>
            
            {(batchId || selectedFile) && !isSubmitting && (
              <Button
                variant="outlined"
                onClick={resetForm}
                disabled={isSubmitting}
                startIcon={<Clear />}
                sx={{ px: 4, py: 1.5, borderRadius: 2 }}
              >
                Clear Form
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {results && (
        <Card elevation={8} sx={{ 
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                color: '#667eea'
              }}>
                📊 Enrollment Results
              </Typography>
              <Button 
                onClick={handleDownload} 
                variant="contained" 
                startIcon={<Download />}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                  fontWeight: 'bold'
                }}
              >
                Download Results
              </Button>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 3, mb: 4 }}>
              <Card sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {results.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Processed</Typography>
              </Card>
              
              <Card sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)' }}>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {results.successful}
                </Typography>
                <Typography variant="body2" color="text.secondary">Successful</Typography>
              </Card>
              
              {results.failed > 0 && (
                <Card sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)' }}>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {results.failed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Failed</Typography>
                </Card>
              )}
            </Box>
            
            {results.results && results.results.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  📋 Detailed Results
                </Typography>
                <Box sx={{ 
                  maxHeight: 400, 
                  overflow: 'auto', 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  backgroundColor: '#fafafa'
                }}>
                  {results.results.map((result, index) => (
                    <Box 
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: index < results.results.length - 1 ? '1px solid #e0e0e0' : 'none',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          📧 {result.candidateId || result.candidateID || 'Unknown ID'}
                        </Typography>
                        {!result.success && (result.error || result.message) && (
                          <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                            ❌ {result.error || result.message}
                          </Typography>
                        )}
                        {result.success && result.message && (
                          <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                            ✅ {result.message}
                          </Typography>
                        )}
                      </Box>
                      <Chip 
                        icon={result.success ? <CheckCircle /> : <Error />}
                        label={result.success ? 'Success' : 'Failed'}
                        color={result.success ? 'success' : 'error'}
                        variant="filled"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BatchEnrollment;
